import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCandidateData } from '../../hooks/useCandidateData';
import ViewResult from "../admin/component/ViewResult";
import api from "../../api/client";
import { getInterviewScore, formatInterviewStatus, formatExpiry, formatInterviewDuration } from '../../utils/candidateUtils';
import CircularProgress from '@mui/material/CircularProgress';
import { 
    TrendingUp, CheckCircle2, Award, Search, Calendar, ChevronDown, 
    ChevronLeft, ChevronRight, Mic, Code, Database, Eye, ClipboardList
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const getTranslatedStatus = (status, t) => {
  if (!status) return t('b2cDashboard.status.unknown', 'Unknown');
  if (status === 'completed') return t('b2cDashboard.status.completed', 'Completed');
  if (status === 'in_progress') return t('b2cDashboard.status.inProgress', 'In Progress');
  if (status === 'pending') return t('b2cDashboard.status.pending', 'Pending');
  
  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const MyProgress = () => {
  const { candidateData, loading } = useCandidateData();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [sessionData, setSessionData] = useState(null);

  const [timeRange, setTimeRange] = useState('last_6_months');
  const [timeRangeOpen, setTimeRangeOpen] = useState(false);

  const [statusFilter, setStatusFilter] = useState('all');
  const [statusOpen, setStatusOpen] = useState(false);

  const [languageFilter, setLanguageFilter] = useState('all');
  const [languageOpen, setLanguageOpen] = useState(false);

  const [sortBy, setSortBy] = useState('newest');
  const [sortOpen, setSortOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleGlobalClick = () => {
      setTimeRangeOpen(false);
      setStatusOpen(false);
      setLanguageOpen(false);
      setSortOpen(false);
    };
    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, []);

  const interviews = candidateData?.interviews || [];
  
  // Calculate Metrics
  const totalInterviews = interviews.length;
  const completedInterviews = interviews.filter(i => formatInterviewStatus(i.status) === 'Completed');
  const completedCount = completedInterviews.length;
  
  const validScores = interviews.map(i => getInterviewScore(i)).filter(s => s !== null);
  const validCount = validScores.length;

  const avgScore = validCount > 0 
    ? (validScores.reduce((acc, score) => acc + score, 0) / validCount).toFixed(2)
    : '0.00';
    
  const bestScore = validCount > 0
    ? Math.max(...validScores).toFixed(2)
    : '0.00';

  // Chart Data based on timeRange
  const chartData = React.useMemo(() => {
    let filteredCompleted = interviews.filter(i => formatInterviewStatus(i.status) === 'Completed');
    
    if (timeRange !== 'all_time') {
      const now = new Date();
      const limitDate = new Date();
      if (timeRange === 'last_7_days') {
        limitDate.setDate(now.getDate() - 7);
      } else if (timeRange === 'last_30_days') {
        limitDate.setDate(now.getDate() - 30);
      } else if (timeRange === 'last_6_months') {
        limitDate.setMonth(now.getMonth() - 6);
      }
      
      filteredCompleted = filteredCompleted.filter(i => {
        const interviewDate = new Date(i.started_at || i.created_at);
        return interviewDate >= limitDate;
      });
    }

    const data = filteredCompleted.map(i => {
      const dateStr = formatExpiry(i.started_at || i.created_at);
      const datePart = dateStr.split(',')[0]; 
      return {
        name: datePart,
        score: getInterviewScore(i) || 0,
        rawDate: new Date(i.started_at || i.created_at || 0)
      };
    });

    // Sort chronologically for the chart
    data.sort((a, b) => a.rawDate - b.rawDate);

    // If there's only 1 item or 0 items, pad it so the line chart renders nicely
    if (data.length === 1) {
      data.unshift({ name: '', score: 0 });
    } else if (data.length === 0) {
      data.push({ name: 'Start', score: 0 }, { name: 'Today', score: 0 });
    }

    return data;
  }, [interviews, timeRange]);

  const pieData = [
    { name: t('b2cDashboard.status.completed', 'Completed'), value: completedCount, color: '#4ade80' },
    { name: t('b2cDashboard.status.pending', 'Pending'), value: totalInterviews - completedCount, color: '#f1f5f9' }
  ];

  // Helper to get icon for job title
  const getJobIcon = (title) => {
    const term = (title || '').toLowerCase();
    if (term.includes('practice')) return <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-500"><Mic className="w-5 h-5" /></div>;
    if (term.includes('frontend') || term.includes('developer')) return <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center text-orange-500"><Code className="w-5 h-5" /></div>;
    if (term.includes('java') || term.includes('backend')) return <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-500"><Code className="w-5 h-5" /></div>;
    if (term.includes('sql') || term.includes('data')) return <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-500"><Database className="w-5 h-5" /></div>;
    return <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center text-gray-500"><ClipboardList className="w-5 h-5" /></div>;
  };

  const filteredInterviews = React.useMemo(() => {
    let result = [...interviews];

    // 1. Search term filter
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      result = result.filter(i => {
        const jobTitle = i.job_description?.title || 'Practice Interview';
        return jobTitle.toLowerCase().includes(query);
      });
    }

    // 2. Status filter
    if (statusFilter !== 'all') {
      result = result.filter(i => {
        const status = formatInterviewStatus(i.status).toLowerCase();
        return status === statusFilter.toLowerCase();
      });
    }

    // 3. Language filter
    if (languageFilter !== 'all') {
      result = result.filter(i => {
        const lang = (i.language || 'English').toLowerCase();
        return lang.includes(languageFilter.toLowerCase());
      });
    }

    // 4. Sorting
    result.sort((a, b) => {
      const dateA = new Date(a.started_at || a.created_at || 0);
      const dateB = new Date(b.started_at || b.created_at || 0);
      
      if (sortBy === 'newest') {
        return dateB - dateA;
      }
      if (sortBy === 'oldest') {
        return dateA - dateB;
      }
      if (sortBy === 'highest_score') {
        return (getInterviewScore(b) || 0) - (getInterviewScore(a) || 0);
      }
      if (sortBy === 'lowest_score') {
        return (getInterviewScore(a) || 0) - (getInterviewScore(b) || 0);
      }
      return 0;
    });

    return result;
  }, [interviews, searchTerm, statusFilter, languageFilter, sortBy]);

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-[#f9fafb]">
        <CircularProgress style={{ color: '#6366f1' }} />
      </div>
    );
  }

  if (!candidateData) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center bg-[#f9fafb] p-6 text-center">
        <ClipboardList className="w-16 h-16 text-gray-400 mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">No Profile Data Found</h2>
        <p className="text-gray-500 max-w-md">We couldn't load your profile information. Please try refreshing the page or logging in again.</p>
      </div>
    );
  }

  const handleViewResults = async (interview) => {
    const sessionId = interview.session_id || interview.id;
    if (!sessionId) return;

    setShowFeedbackModal(true);
    setLoadingFeedback(true);
    setFeedbackError("");
    setFeedback(null);

    try {
      const sessionRes = await api.get(`sessions/${sessionId}/`, {
        headers: { Authorization: `Token ${localStorage.getItem("adminToken")}` }
      });
      setSessionData(sessionRes.data);

      const feedbackRes = await api.get(`feedback/?session=${sessionId}`, {
        headers: { Authorization: `Token ${localStorage.getItem("adminToken")}` }
      });

      if (!feedbackRes.data || !feedbackRes.data.results || feedbackRes.data.results.length === 0) {
        if (interview.feedback) {
          setFeedback(interview.feedback);
        } else {
          setFeedbackError("No feedback found for this session.");
        }
        return;
      }

      setFeedback(feedbackRes.data.results[0]);
    } catch {
      if (interview.feedback) {
        setFeedback(interview.feedback);
        setSessionData({
          id: interview.id,
          started_at: interview.started_at,
          ended_at: interview.ended_at,
          duration_minutes: interview.duration_minutes,
          status: interview.status
        });
      } else {
        setFeedbackError("Failed to load feedback.");
      }
    } finally {
      setLoadingFeedback(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#fafafc] pb-12 overflow-hidden" style={{ fontFamily: "'Inter', 'Poppins', sans-serif" }}>
      <div className="p-4 sm:p-6 w-full">
      
        {/* Header Area */}
        <div className="mb-6">
          <div
            className="w-full px-4 py-3 rounded-xl text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
          >
            <div>
              <h1 className="text-lg font-semibold text-white mb-0.5 flex items-center gap-2">
                 <TrendingUp className="text-white w-5 h-5" />
                 {t('b2cDashboard.myInterviewJourney', 'My Interview Journey')}
              </h1>
              <p className="text-xs text-blue-100 font-medium">{t('b2cDashboard.myJourneySubtitle', 'Track your interview performance, review feedback, and monitor your improvement over time.')}</p>
            </div>
          </div>
        </div>

        <div className="w-full">
        
        {/* Top Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500 shrink-0">
              <ClipboardList className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-0.5 line-clamp-1" title="Total Interviews">{t('b2cDashboard.interviewsTaken', 'Total Interviews')}</div>
              <div className="flex items-baseline gap-1.5">
                <div className="text-xl font-bold text-gray-900 leading-none">{totalInterviews}</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center text-green-500 shrink-0">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-0.5 line-clamp-1" title="Completed">{t('b2cDashboard.status.completed', 'Completed')}</div>
              <div className="flex items-baseline gap-1.5">
                <div className="text-xl font-bold text-gray-900 leading-none">{completedCount}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center text-purple-500 shrink-0">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-0.5 line-clamp-1" title="Avg Score">{t('b2cDashboard.averageScoreLabel', 'Avg Score')}</div>
              <div className="flex items-baseline gap-1.5">
                <div className="text-xl font-bold text-gray-900 leading-none">{avgScore}%</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500 shrink-0">
              <Award className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-0.5 line-clamp-1" title="Best Score">{t('b2cDashboard.bestScoreLabel', 'Best Score')}</div>
              <div className="flex items-baseline gap-1.5">
                <div className="text-xl font-bold text-gray-900 leading-none">{bestScore}%</div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Line Chart */}
          <div className="md:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-bold text-gray-800 flex items-center gap-1.5">
                {t('b2cDashboard.performanceTrend', 'Performance Trend')}
                <div className="w-3.5 h-3.5 rounded-full border border-gray-300 text-gray-400 flex items-center justify-center text-[8px] cursor-help" title={t('b2cDashboard.scoreOverTime', 'Score over time')}>i</div>
              </h3>
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setTimeRangeOpen(!timeRangeOpen);
                    setStatusOpen(false);
                    setLanguageOpen(false);
                    setSortOpen(false);
                  }}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 flex items-center gap-2 cursor-pointer hover:bg-gray-50 bg-white"
                >
                  {timeRange === 'last_7_days' && t('b2cDashboard.timeRange.last7Days', 'Last 7 Days')}
                  {timeRange === 'last_30_days' && t('b2cDashboard.timeRange.last30Days', 'Last 30 Days')}
                  {timeRange === 'last_6_months' && t('b2cDashboard.last6Months', 'Last 6 Months')}
                  {timeRange === 'all_time' && t('b2cDashboard.timeRange.allTime', 'All Time')}
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {timeRangeOpen && (
                  <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 text-xs font-semibold text-gray-600">
                    {[
                      { value: 'last_7_days', label: t('b2cDashboard.timeRange.last7Days', 'Last 7 Days') },
                      { value: 'last_30_days', label: t('b2cDashboard.timeRange.last30Days', 'Last 30 Days') },
                      { value: 'last_6_months', label: t('b2cDashboard.last6Months', 'Last 6 Months') },
                      { value: 'all_time', label: t('b2cDashboard.timeRange.allTime', 'All Time') }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setTimeRange(opt.value);
                          setTimeRangeOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${timeRange === opt.value ? 'text-indigo-600 bg-indigo-50/50' : ''}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 min-h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b', fontWeight: 600 }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b', fontWeight: 600 }} ticks={[0, 25, 50, 75, 100]} tickFormatter={(val) => `${val}%`} />
                  <RechartsTooltip 
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    itemStyle={{ color: '#4f46e5', fontWeight: 'bold' }}
                    formatter={(value) => [`${value}%`, t('common.score', 'Score')]}
                  />
                  <Line type="monotone" dataKey="score" stroke="#8b5cf6" strokeWidth={3} dot={{ r: 4, strokeWidth: 2, fill: '#fff', stroke: '#8b5cf6' }} activeDot={{ r: 6, fill: '#8b5cf6', stroke: '#fff', strokeWidth: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Pie Chart */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col">
            <h3 className="font-bold text-gray-800 mb-6">{t('b2cDashboard.interviewsByStatus', 'Interviews by Status')}</h3>
            <div className="flex-1 flex flex-col items-center justify-center relative">
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    paddingAngle={0}
                    dataKey="value"
                    stroke="none"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xl font-black text-gray-900 leading-none">{totalInterviews}</span>
                <span className="text-[10px] font-bold text-gray-500">{t('b2cDashboard.total', 'Total')}</span>
              </div>
              <div className="w-full flex justify-between items-center mt-6 px-4">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 bg-green-400 rounded-sm"></div>
                  <span className="text-xs font-bold text-gray-700">{t('b2cDashboard.status.completed', 'Completed')}</span>
                </div>
                <div className="text-xs font-bold text-gray-500">
                  {completedCount} ({totalInterviews > 0 ? Math.round((completedCount/totalInterviews)*100) : 0}%)
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="flex flex-col sm:flex-row items-center gap-3 mb-6">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input 
              type="text" 
              placeholder={t('b2cDashboard.searchInterviews', 'Search interviews...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 font-medium"
            />
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">

            {/* Status Dropdown */}
            <div className="relative flex-1 sm:flex-none">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setStatusOpen(!statusOpen);
                  setTimeRangeOpen(false);
                  setLanguageOpen(false);
                  setSortOpen(false);
                }}
                className="w-full sm:w-auto px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-600 flex items-center justify-between gap-2 hover:bg-gray-50"
              >
                {statusFilter === 'all' && t('b2cDashboard.allStatus', 'All Status')}
                {statusFilter === 'completed' && t('b2cDashboard.status.completed', 'Completed')}
                {statusFilter === 'in_progress' && t('b2cDashboard.status.inProgress', 'In Progress')}
                {statusFilter === 'pending' && t('b2cDashboard.status.pending', 'Pending')}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {statusOpen && (
                <div className="absolute left-0 sm:right-0 mt-1 w-40 bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-50 text-xs font-semibold text-gray-600">
                  {[
                    { value: 'all', label: t('b2cDashboard.allStatus', 'All Status') },
                    { value: 'completed', label: t('b2cDashboard.status.completed', 'Completed') },
                    { value: 'in_progress', label: t('b2cDashboard.status.inProgress', 'In Progress') },
                    { value: 'pending', label: t('b2cDashboard.status.pending', 'Pending') }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setStatusFilter(opt.value);
                        setStatusOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 ${statusFilter === opt.value ? 'text-indigo-600 bg-indigo-50/50' : ''}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Language Dropdown */}
            <div className="relative flex-1 sm:flex-none">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLanguageOpen(!languageOpen);
                  setTimeRangeOpen(false);
                  setStatusOpen(false);
                  setSortOpen(false);
                }}
                className="w-full sm:w-auto px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-600 flex items-center justify-between gap-2 hover:bg-gray-50"
              >
                {languageFilter === 'all' && t('b2cDashboard.allLanguages', 'All Languages')}
                {languageFilter === 'english' && t('languages.en', 'English')}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {languageOpen && (
                <div className="absolute left-0 sm:right-0 mt-1 w-40 bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-50 text-xs font-semibold text-gray-600">
                  {[
                    { value: 'all', label: t('b2cDashboard.allLanguages', 'All Languages') },
                    { value: 'english', label: t('languages.en', 'English') }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setLanguageFilter(opt.value);
                        setLanguageOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 ${languageFilter === opt.value ? 'text-indigo-600 bg-indigo-50/50' : ''}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="relative flex-1 sm:flex-none">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSortOpen(!sortOpen);
                  setTimeRangeOpen(false);
                  setStatusOpen(false);
                  setLanguageOpen(false);
                }}
                className="w-full sm:w-auto px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-600 flex items-center justify-between gap-2 hover:bg-gray-50"
              >
                <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span className="truncate">
                  {sortBy === 'newest' && t('b2cDashboard.newestFirst', 'Newest First')}
                  {sortBy === 'oldest' && t('b2cDashboard.sort.oldestFirst', 'Oldest First')}
                  {sortBy === 'highest_score' && t('b2cDashboard.sort.highestScore', 'Highest Score')}
                  {sortBy === 'lowest_score' && t('b2cDashboard.sort.lowestScore', 'Lowest Score')}
                </span>
                <ChevronDown className="w-3.5 h-3.5 shrink-0" />
              </button>
              {sortOpen && (
                <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-50 text-xs font-semibold text-gray-600">
                  {[
                    { value: 'newest', label: t('b2cDashboard.newestFirst', 'Newest First') },
                    { value: 'oldest', label: t('b2cDashboard.sort.oldestFirst', 'Oldest First') },
                    { value: 'highest_score', label: t('b2cDashboard.sort.highestScore', 'Highest Score') },
                    { value: 'lowest_score', label: t('b2cDashboard.sort.lowestScore', 'Lowest Score') }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setSortBy(opt.value);
                        setSortOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 ${sortBy === opt.value ? 'text-indigo-600 bg-indigo-50/50' : ''}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <h3 className="font-extrabold text-gray-900 mb-4">{t('b2cDashboard.interviewHistory', 'Interview History')}</h3>

        {/* Interview List */}
        <div className="flex flex-col space-y-3">
          {filteredInterviews.length > 0 ? (
            filteredInterviews.map((interview, idx) => {
              const score = getInterviewScore(interview);
              const status = getTranslatedStatus(interview.status, t);
              const date = formatExpiry(interview.started_at || interview.created_at);
              const jobTitle = interview.job_description?.title || t('b2cDashboard.practiceInterview', 'Practice Interview');
              const duration = formatInterviewDuration(interview);

              const scoreValue = score !== null ? score : 0;
              const scoreColor = scoreValue >= 70 ? '#16a34a' : scoreValue >= 40 ? '#d97706' : '#dc2626';

              const langCode = (interview.language || 'en').toLowerCase();
              const langName = t(`languages.${langCode}`, langCode.toUpperCase());

              return (
                <div key={idx} className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-100 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 sm:gap-6 hover:border-indigo-100 hover:shadow-md transition-all group">
                  
                  {/* Left Section: Icon + Title */}
                  <div className="flex items-center gap-4 min-w-[240px]">
                    {getJobIcon(jobTitle)}
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1">{jobTitle}</h4>
                      <p className="text-xs text-gray-500 mb-1">
                        {jobTitle.toLowerCase().includes('practice') ? t('b2cDashboard.practiceInterview', 'Practice Interview') : t('b2cDashboard.types.technical', 'Technical Interview')}
                      </p>
                      <div className="flex items-center gap-1.5 text-[11px] text-gray-400 font-medium">
                        <Calendar className="w-3 h-3" />
                        {date}
                      </div>
                    </div>
                  </div>

                  {/* Middle Section: Language */}
                  <div className="hidden sm:block">
                    <div className="flex items-center gap-1.5 text-blue-600 font-bold text-xs mb-1">
                      <div className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center text-[8px]">🌐</div>
                      {langName} ({langCode.toUpperCase()})
                    </div>
                    <div className="text-[11px] text-gray-400 font-medium ml-5">{t('common.language', 'Language')}</div>
                  </div>

                  {/* Status & Duration */}
                  <div className="hidden sm:block">
                    <div className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold items-center gap-1.5 mb-1.5 ${interview.status === 'completed' ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-yellow-50 text-yellow-600 border border-yellow-100'}`}>
                      {interview.status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
                      {status}
                    </div>
                    <div>
                      <span className="text-[11px] text-gray-400 font-medium block leading-tight">{t('common.duration', 'Duration')}</span>
                      <span className="text-xs font-bold text-gray-700 leading-tight">{duration}</span>
                    </div>
                  </div>

                  {/* Score */}
                  <div className="hidden sm:flex flex-col items-center">
                    <div className="text-lg font-black leading-tight" style={{ color: scoreColor }}>
                      {scoreValue.toFixed(2)}%
                    </div>
                    <div className="text-[10px] text-gray-400 font-medium">{t('common.score', 'Score')}</div>
                    <div className="text-[10px] text-gray-500 font-bold">{Math.round(scoreValue)} / 100</div>
                  </div>

                  {/* Action Button */}
                  <div className="w-full md:w-auto mt-2 md:mt-0 flex justify-end">
                    <button 
                      onClick={() => handleViewResults(interview)}
                      disabled={interview.status !== "completed"}
                      className={`w-full sm:w-auto px-4 py-2 border-2 rounded-xl text-xs font-extrabold flex items-center justify-center gap-2 transition-colors ${
                        interview.status === "completed"
                          ? "border-indigo-100 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-200"
                          : "border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed"
                      }`}
                    >
                      <Eye className="w-3.5 h-3.5" />
                      {t('b2cDashboard.viewFeedback', 'View Feedback')}
                    </button>
                  </div>

                </div>
              );
            })
          ) : (
            <div className="py-12 bg-white rounded-2xl border border-gray-100 text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClipboardList className="w-6 h-6 text-gray-300" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">{t('candidate.noInterviews', 'No interviews found')}</h3>
              <p className="text-gray-500 text-sm">{t('b2cDashboard.noInterviewsMatchingSearch', "We couldn't find any interviews matching your search.")}</p>
            </div>
          )}
        </div>

        {/* Pagination Mock */}
        {filteredInterviews.length > 0 && (
          <div className="flex justify-center items-center gap-2 mt-8">
            <button className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-50">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button className="w-8 h-8 rounded-lg bg-indigo-600 text-white font-bold text-sm flex items-center justify-center shadow-sm">
              1
            </button>
            <button className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-50">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

      </div>
      </div>

      {showFeedbackModal && (
        <ViewResult
          feedback={feedback}
          loadingFeedback={loadingFeedback}
          feedbackError={feedbackError}
          onClose={() => setShowFeedbackModal(false)}
          sessionData={sessionData}
          isSendingEmail=""
          emailSent=""
          sendEmail=""
          callFrom="interviewSection"
        />
      )}

    </div>
  );
};

export default MyProgress;
